import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Sparkles, Loader2, Save, Plus, Trash2, Trash, Layers,
    FileText, AlertTriangle, CheckCircle2, Hash, Clock, Info,
    Maximize2, X, Target, Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import * as apiClient from '@/services/apiClient';
import ConfirmDialog from '@/components/ux/ConfirmDialog';
import ScreeningSkillsEditor from '@/components/templates/ScreeningSkillsEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

/**
 * Create and edit a job description on its own page.
 *
 * This was a 396-line dialog holding AI generation, the description, screening
 * skills and the interview-rounds builder. Rounds are where people get stuck,
 * and a modal gave them no room. The right column keeps a running summary so
 * it is clear whether the job description is ready to schedule against.
 */

const EMPTY_FORM = { title: '', jobDescription: '', department: '', position: '', difficultyLevel: '', screeningSkills: [], passThreshold: '' };
const EMPTY_AI = { roleTitle: '', experienceLevel: '', requiredSkills: '', mustHaveRequirements: '', additionalContext: '' };
const EMPTY_ROUND = {
    roundMasterId: '',
    interviewTypeMasterId: '',
    difficultyMasterId: '',
    numberOfQuestions: '8',
    durationMinutes: '30',
};
// Common employment types, offered as suggestions rather than a fixed list —
// anything can still be typed.
const POSITION_OPTIONS = [
    'Full-time', 'Part-time', 'Contract', 'Internship',
    'Temporary', 'Freelance', 'Consultant', 'Apprenticeship',
];

const EXPERIENCE_LEVELS = ['Intern', 'Junior', 'Mid-Level', 'Senior', 'Lead', 'Principal'];
const DIFFICULTY_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'beginner', label: 'Easy' },
    { value: 'intermediate', label: 'Medium' },
    { value: 'advanced', label: 'Hard' },
];

// Server-side caps, mirrored so the counter shows the real limit.
const AI_LIMITS = { requiredSkills: 1000, mustHaveRequirements: 2000, additionalContext: 1000 };

/**
 * Placeholder examples matched to the role being described.
 *
 * A single engineering example leaves HR guessing what to write for a sales,
 * support or finance role, so the examples follow the role title and fall back
 * to something deliberately non-technical.
 */
const ROLE_PLACEHOLDERS = [
    { match: /engineer|developer|programmer|software|frontend|backend|full.?stack|devops|sre/i,
      skills: 'React, Node.js, PostgreSQL, AWS, Docker',
      mustHave: '3+ years building production web applications\nStrong grasp of REST APIs and relational databases\nB.Tech in Computer Science or equivalent experience',
      context: 'FinTech startup, fully remote, agile team of 12' },
    { match: /data|analyst|scientist|\bml\b|machine learning|\bai\b/i,
      skills: 'Python, SQL, Pandas, Power BI, Statistics',
      mustHave: '2+ years turning raw data into business reporting\nComfortable writing complex SQL joins and window functions\nDegree in Statistics, Mathematics or similar',
      context: 'Reports into the Head of Analytics, hybrid, 3 days in office' },
    { match: /sales|business development|account|\bbd\b|revenue/i,
      skills: 'B2B Sales, CRM, Negotiation, Lead Generation, Cold Outreach',
      mustHave: '3+ years closing B2B deals with an annual quota\nTrack record of hitting or beating target two years running\nComfortable owning a pipeline end to end',
      context: 'SaaS company selling to mid-market HR teams, target-driven' },
    { match: /support|customer|service|success|helpdesk|chat/i,
      skills: 'Customer Handling, Written Communication, Empathy, CRM, Escalation Management',
      mustHave: '2+ years in a customer-facing support role\nExcellent written English, able to handle 30+ chats a day\nExperience with ticketing tools such as Zendesk or Freshdesk',
      context: '24x7 rotational shifts, chat and email support, CSAT-driven' },
    { match: /\bhr\b|human resource|recruit|talent|people/i,
      skills: 'Recruitment, Stakeholder Management, Onboarding, HRMS, Employee Relations',
      mustHave: '3+ years recruiting for technical and non-technical roles\nExperience running the full cycle from sourcing to offer\nMBA in HR or equivalent experience',
      context: 'Growing team of 200, hiring across engineering and operations' },
    { match: /finance|account|audit|tax|payroll/i,
      skills: 'Financial Reporting, Excel, Tally, GST, Reconciliation',
      mustHave: '3+ years in accounts payable and receivable\nHands-on with monthly closing and statutory compliance\nCA Inter, M.Com or equivalent',
      context: 'In-house finance team, month-end close ownership' },
    { match: /market|content|seo|brand|social/i,
      skills: 'SEO, Content Strategy, Google Analytics, Copywriting, Campaign Management',
      mustHave: '2+ years owning content or performance campaigns\nAble to show measurable growth from work you led\nStrong written English',
      context: 'D2C brand, small marketing team, heavy on organic growth' },
    { match: /design|\bux\b|\bui\b|graphic/i,
      skills: 'Figma, User Research, Prototyping, Design Systems, Accessibility',
      mustHave: '3+ years designing digital products end to end\nA portfolio showing your process, not just final screens\nComfortable working directly with engineers',
      context: 'Product team of 20, design owns research through handoff' },
    { match: /operation|logistic|supply|warehouse|process/i,
      skills: 'Process Improvement, SLA Management, Reporting, Vendor Management, Excel',
      mustHave: '3+ years running day-to-day operations for a team\nExperience owning SLAs and productivity targets\nComfortable with data-led decision making',
      context: 'Multi-site operations, strong focus on SLA and cost per unit' },
];

const DEFAULT_PLACEHOLDERS = {
    skills: 'List the main skills, separated by commas',
    mustHave: 'What a candidate must have to be considered.\ne.g. 3+ years in a similar role\nA relevant degree or equivalent experience',
    context: 'Anything else that shapes the role — team size, shift pattern, location, reporting line',
};

function placeholdersForRole(roleTitle) {
    return ROLE_PLACEHOLDERS.find(r => r.match.test(String(roleTitle || ''))) || DEFAULT_PLACEHOLDERS;
}

/**
 * Renders the generated markdown as readable text.
 *
 * The AI returns headings, bullets and bold runs; shown raw it is a wall of
 * asterisks and hashes that nobody wants to proofread. Deliberately a small
 * renderer rather than a markdown dependency — the output shape is known and
 * narrow, and the text is escaped rather than injected as HTML.
 */
function JDPreview({ text }) {
    const lines = String(text || '').split(/\r?\n/);
    const blocks = [];
    let bullets = [];

    const flush = () => {
        if (!bullets.length) return;
        blocks.push(
            <ul key={`u${blocks.length}`} className="my-2 list-disc space-y-1 pl-5 text-sm text-foreground/90">
                {bullets.map((b, i) => <li key={i}>{inline(b)}</li>)}
            </ul>,
        );
        bullets = [];
    };

    // Only **bold** is worth honouring; everything else reads fine as text.
    function inline(str) {
        return String(str).split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
            /^\*\*[^*]+\*\*$/.test(part)
                ? <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>
                : <span key={i}>{part}</span>,
        );
    }

    lines.forEach((raw, idx) => {
        const line = raw.trimEnd();
        const heading = line.match(/^(#{1,6})\s+(.*)$/);
        const bullet  = line.match(/^\s*[-*•]\s+(.*)$/);

        if (heading) {
            flush();
            const level = heading[1].length;
            blocks.push(
                <p key={idx} className={cn('mt-4 first:mt-0 font-semibold text-foreground',
                    level <= 2 ? 'text-base' : 'text-sm')}>
                    {inline(heading[2])}
                </p>,
            );
        } else if (bullet) {
            bullets.push(bullet[1]);
        } else if (!line.trim()) {
            flush();
        } else {
            flush();
            // A short bold-only line is a heading in all but syntax.
            const bare = line.replace(/\*\*/g, '');
            const isPseudoHeading = /^\*\*.+\*\*:?$/.test(line.trim()) && bare.length < 60;
            blocks.push(
                <p key={idx} className={cn(
                    isPseudoHeading ? 'mt-4 first:mt-0 text-sm font-semibold text-foreground'
                                    : 'mt-2 text-sm leading-relaxed text-foreground/90',
                )}>
                    {inline(line)}
                </p>,
            );
        }
    });
    flush();

    if (!blocks.length) {
        return <p className="text-sm text-muted-foreground">Nothing to preview yet.</p>;
    }
    return <div>{blocks}</div>;
}

const MASTER_KIND = {
    round:         { label: 'round',          placeholder: 'e.g. Culture Fit' },
    interviewType: { label: 'interview type', placeholder: 'e.g. Client Communication' },
    difficulty:    { label: 'difficulty',     placeholder: 'e.g. Expert' },
};

/**
 * Adds a master without leaving the job description.
 *
 * Only a name is asked for — slug and sort order are derived server-side, the
 * same as the Masters page. The entry is created company-wide, because rounds
 * are stored by reference and cannot be free text on one job description.
 */
function InlineAddMaster({ type, onCreated, disabled }) {
    const [open, setOpen]     = useState(false);
    const [name, setName]     = useState('');
    const [saving, setSaving] = useState(false);
    const kind = MASTER_KIND[type];

    const submit = async () => {
        const trimmed = name.trim();
        if (trimmed.length < 2) { toast.error('Enter a name of at least 2 characters.'); return; }
        setSaving(true);
        try {
            const res = await apiClient.createMaster({ type, name: trimmed });
            toast.success(`"${trimmed}" added to Interview Masters.`);
            setOpen(false); setName('');
            onCreated(res.data);
        } catch (err) {
            toast.error(err.message || `Could not add that ${kind.label}.`);
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen(true)}
                className="text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
            >
                + Add {kind.label}
            </button>
        );
    }

    return (
        <div className="flex items-center gap-1">
            <Input
                autoFocus
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => {
                    // Enter must not submit the job description form behind this.
                    if (e.key === 'Enter') { e.preventDefault(); submit(); }
                    if (e.key === 'Escape') { setOpen(false); setName(''); }
                }}
                placeholder={kind.placeholder}
                className="h-7 flex-1 text-xs"
            />
            <Button type="button" size="sm" className="h-7 px-2 text-[11px]" onClick={submit} disabled={saving}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Add'}
            </Button>
            <Button
                type="button" variant="ghost" size="icon" className="h-7 w-7"
                onClick={() => { setOpen(false); setName(''); }}
            >
                <X className="h-3 w-3" />
            </Button>
        </div>
    );
}

/** Field label with a live "used / allowed" counter that warns as it fills up. */
function CharCount({ value, max }) {
    const used = (value || '').length;
    const left = max - used;
    return (
        <span className={cn(
            'text-[11px] tabular-nums',
            left < 0 ? 'font-semibold text-destructive'
                : left < max * 0.1 ? 'text-amber-600 dark:text-amber-500'
                : 'text-muted-foreground',
        )}>
            {left < 0 ? `${Math.abs(left).toLocaleString()} over the ${max.toLocaleString()} limit`
                      : `${used.toLocaleString()} / ${max.toLocaleString()} · ${left.toLocaleString()} left`}
        </span>
    );
}

function templateRoundFromApi(r) {
    return {
        roundMasterId: r.roundMasterId?._id || r.roundMasterId || '',
        interviewTypeMasterId: r.interviewTypeMasterId?._id || r.interviewTypeMasterId || '',
        difficultyMasterId: r.difficultyMasterId?._id || r.difficultyMasterId || '',
        numberOfQuestions: String(r.numberOfQuestions ?? 8),
        durationMinutes: String(r.durationMinutes ?? 30),
    };
}

/**
 * Compact page header for the form pages.
 *
 * The shared PageHeader carries mb-8, pb-6 and a rule — around 80px before any
 * field, which is right for a list page and wasteful on a form someone fills in
 * regularly. Back arrow and title sit on one line here.
 */
function FormHeader({ title, subtitle, onBack }) {
    return (
        <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                onClick={onBack} aria-label="Back">
                <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
                <h1 className="truncate text-base font-semibold tracking-tight text-foreground">{title}</h1>
                <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
            </div>
        </div>
    );
}

export default function TemplateFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [editingTemplate, setEditingTemplate] = useState(null);
    const [loadingTemplate, setLoadingTemplate] = useState(isEdit);

    const [form, setForm] = useState(EMPTY_FORM);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');

    const [useAI, setUseAI] = useState(false);
    const [jdView, setJdView] = useState('edit');       // 'edit' | 'preview'
    const [jdExpanded, setJdExpanded] = useState(false);
    // Skills the AI found in the JD text — offered as one-click additions.
    const [jdAnalysis, setJdAnalysis] = useState(null);
    const [analysing, setAnalysing]   = useState(false);
    const [aiForm, setAiForm] = useState(EMPTY_AI);
    const [generatingJD, setGeneratingJD] = useState(false);
    const [confirmGenerate, setConfirmGenerate] = useState(false);

    const [interviewRounds, setInterviewRounds] = useState([{ ...EMPTY_ROUND }]);
    const [masters, setMasters] = useState({ round: [], difficulty: [], interviewType: [] });
    const [mastersLoading, setMastersLoading] = useState(false);

    const [pipelineRoundConfirmOpen, setPipelineRoundConfirmOpen] = useState(false);
    const [pendingSavePayload, setPendingSavePayload] = useState(null);

    const [companyDefault, setCompanyDefault] = useState(null);
    useEffect(() => {
        apiClient.getInterviewSettings()
            .then(res => {
                const v = res?.data?.passThreshold;
                if (typeof v === 'number') setCompanyDefault(v);
            })
            .catch(() => {});   // no manage_settings — the hint stays generic
    }, []);

    // ── Load masters (round types, difficulties, interview types) ─────────────
    useEffect(() => {
        setMastersLoading(true);
        apiClient.getMasters()
            .then(res => {
                const d = res.data || {};
                setMasters({
                    round: (d.round || []).filter(m => m.isActive),
                    difficulty: (d.difficulty || []).filter(m => m.isActive),
                    interviewType: (d.interviewType || []).filter(m => m.isActive),
                });
            })
            .catch(() => {})
            .finally(() => setMastersLoading(false));
    }, []);

    // ── Load the job description being edited ─────────────────────────────────
    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;
        apiClient.getTemplates()
            .then(res => {
                if (cancelled) return;
                const tpl = (res.data || []).find(t => t._id === id);
                if (!tpl) { toast.error('Job description not found.'); navigate('/admin/templates'); return; }
                setEditingTemplate(tpl);
                setForm({
                    title: tpl.title,
                    jobDescription: tpl.jobDescription,
                    department: tpl.department || '',
                    position: tpl.position || '',
                    difficultyLevel: tpl.difficultyLevel || '',
                    screeningSkills: (tpl.screeningSkills || []).map(s => ({ name: s.name, required: Boolean(s.required) })),
                    // Blank means inherit; an explicit 0 must survive the round trip.
                    passThreshold: tpl.passThreshold === null || tpl.passThreshold === undefined ? '' : String(tpl.passThreshold),
                });
                setInterviewRounds(tpl.interviewRounds?.length
                    ? tpl.interviewRounds.map(templateRoundFromApi)
                    : [{ ...EMPTY_ROUND }]);
            })
            .catch(() => { if (!cancelled) toast.error('Could not load that job description.'); })
            .finally(() => { if (!cancelled) setLoadingTemplate(false); });
        return () => { cancelled = true; };
    }, [id, isEdit, navigate]);

    const usedRoundMasterIds = (excludeIndex) =>
        new Set(interviewRounds.map((r, i) => (i !== excludeIndex && r.roundMasterId ? r.roundMasterId : null)).filter(Boolean));

    const addInterviewRound = () => {
        setInterviewRounds(prev => [...prev, { ...EMPTY_ROUND }]);
    };

    const removeInterviewRound = (index) => {
        setInterviewRounds(prev => prev.length <= 1 ? prev : prev.filter((_, i) => i !== index));
    };

    /** Adds a newly created master to the list and selects it on this round. */
    const handleMasterCreated = (type, master, roundIndex, field) => {
        setMasters(prev => ({ ...prev, [type]: [...prev[type], master] }));
        updateInterviewRound(roundIndex, field, master._id);
    };

    const updateInterviewRound = (index, field, value) => {
        setInterviewRounds(prev => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
    };

    const isPipelineRoundBlockedError = (err) =>
        err?.code === 'PIPELINE_ROUND_CHANGE_BLOCKED'
        || /pipeline/i.test(err?.message || '');

    const saveTemplate = async (payload, { confirmPipelineRoundChange = false } = {}) => {
        const body = confirmPipelineRoundChange
            ? { ...payload, confirmPipelineRoundChange: true }
            : payload;

        if (editingTemplate) {
            await apiClient.updateTemplate(editingTemplate._id, body);
            toast.success(`"${payload.title}" updated.`);
        } else {
            await apiClient.createTemplate(body);
            toast.success(`"${payload.title}" created.`);
        }

        setPipelineRoundConfirmOpen(false);
        setPendingSavePayload(null);
        navigate('/admin/templates');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        const trimmedTitle = form.title.trim();
        const trimmedJD    = form.jobDescription.trim();

        if (!trimmedTitle) { setFormError('Title is required.'); return; }
        if (trimmedTitle.length < 2) { setFormError('Title must be at least 2 characters.'); return; }
        if (!trimmedJD) { setFormError('Job description is required.'); return; }
        if (trimmedJD.length < 50) { setFormError('Job description must be at least 50 characters.'); return; }

        const roundsPayload = [];
        for (let i = 0; i < interviewRounds.length; i++) {
            const r = interviewRounds[i];
            if (!r.roundMasterId || !r.interviewTypeMasterId || !r.difficultyMasterId) {
                setFormError(`Round ${i + 1}: select round, interview type, and difficulty.`);
                return;
            }
            const nq = Number(r.numberOfQuestions);
            const dm = Number(r.durationMinutes);
            if (!nq || nq < 1 || nq > 30) { setFormError(`Round ${i + 1}: questions must be 1–30.`); return; }
            if (!dm || dm < 5 || dm > 240) { setFormError(`Round ${i + 1}: duration must be 5–240 minutes.`); return; }
            roundsPayload.push({
                roundMasterId: r.roundMasterId,
                interviewTypeMasterId: r.interviewTypeMasterId,
                difficultyMasterId: r.difficultyMasterId,
                numberOfQuestions: nq,
                durationMinutes: dm,
            });
        }

        const threshold = String(form.passThreshold ?? '').trim();
        if (threshold !== '') {
            const n = Number(threshold);
            if (!Number.isFinite(n) || n < 0 || n > 100) {
                setFormError('Pass threshold must be between 0 and 100, or left blank to use the company default.');
                return;
            }
        }

        const payload = {
            ...form,
            title: trimmedTitle,
            jobDescription: trimmedJD,
            interviewRounds: roundsPayload,
            // Blank sends null, which means "inherit the company setting".
            passThreshold: threshold === '' ? null : Number(threshold),
        };

        setFormLoading(true);
        try {
            await saveTemplate(payload);
        } catch (err) {
            if (editingTemplate && isPipelineRoundBlockedError(err)) {
                setPendingSavePayload(payload);
                setPipelineRoundConfirmOpen(true);
                setFormError('');
            } else {
                setFormError(err.message || `Failed to ${editingTemplate ? 'update' : 'create'} template.`);
            }
        } finally {
            setFormLoading(false);
        }
    };

    const handleConfirmPipelineRoundChange = async () => {
        if (!pendingSavePayload) return;
        setFormLoading(true);
        try {
            await saveTemplate(pendingSavePayload, { confirmPipelineRoundChange: true });
        } catch (err) {
            setFormError(err.message || 'Failed to update template.');
            setPipelineRoundConfirmOpen(false);
        } finally {
            setFormLoading(false);
        }
    };

    /** Runs the same analysis on a job description that was written by hand. */
    const handleAnalyseJD = async () => {
        const jd = form.jobDescription.trim();
        if (jd.length < 50) { toast.error('Write at least 50 characters first.'); return; }
        setAnalysing(true);
        try {
            const res = await apiClient.analyseJobDescription(jd);
            setJdAnalysis(res.data);
            const found = (res.data?.mustHaveSkills?.length || 0) + (res.data?.niceToHaveSkills?.length || 0);
            toast.success(found ? `Found ${found} skill${found === 1 ? '' : 's'} in this description.` : 'No clear skills found — add them by hand.');
        } catch (err) {
            toast.error(err.message || 'Could not analyse the job description.');
        } finally {
            setAnalysing(false);
        }
    };

    const handleGenerateJD = async () => {
        const role = aiForm.roleTitle.trim();
        if (!role) { toast.error('Enter a role title first.'); return; }
        if (role.length < 2) { toast.error('Role title must be at least 2 characters.'); return; }

        // Warn before overwriting existing JD
        if (form.jobDescription.trim() && !confirmGenerate) {
            setConfirmGenerate(true);
            toast.warning('Job description already has content. Click Generate again to overwrite it.');
            return;
        }
        setConfirmGenerate(false);

        setGeneratingJD(true);
        try {
            const res = await apiClient.generateJobDescription({
                roleTitle:            role,
                experienceLevel:      aiForm.experienceLevel,
                requiredSkills:       aiForm.requiredSkills,
                mustHaveRequirements: aiForm.mustHaveRequirements,
                additionalContext:    aiForm.additionalContext,
            });
            setForm(f => ({ ...f, jobDescription: res.data.jobDescription, title: f.title || res.data.roleTitle }));
            if (res.data.analysis) setJdAnalysis(res.data.analysis);
            toast.success('Job description generated. Review and edit before saving.');
        } catch (err) {
            const status = err.status || err.statusCode;
            if (status === 503 || status === 529 || /overload|rate.limit/i.test(err.message || '')) {
                toast.error('AI is temporarily overloaded. Please try again in a moment.');
            } else if (!navigator.onLine || /fetch|network/i.test(err.message || '')) {
                toast.error('Network error. Check your connection and try again.');
            } else {
                toast.error(err.message || 'Failed to generate job description. Please try again.');
            }
        } finally {
            setGeneratingJD(false);
        }
    };


    /**
     * Skills offered for one-click adding.
     *
     * A fresh scan wins; otherwise fall back to whatever was cached when the
     * job description was last saved, so editing an existing role still shows
     * suggestions without spending another AI call.
     */
    const jdSuggestions = useMemo(() => {
        const source = jdAnalysis || editingTemplate?.jdCache;
        if (!source) return [];
        const seen = new Set();
        const out  = [];
        for (const [list, required] of [[source.mustHaveSkills, true], [source.niceToHaveSkills, false]]) {
            for (const raw of list || []) {
                const name = String(raw).trim();
                const key  = name.toLowerCase();
                if (!name || seen.has(key)) continue;
                seen.add(key);
                out.push({ name, required });
            }
        }
        return out;
    }, [jdAnalysis, editingTemplate]);

    const addSuggestedSkill = (name, required) => {
        setForm(f => {
            if ((f.screeningSkills || []).some(x => x.name.toLowerCase() === name.toLowerCase())) return f;
            return { ...f, screeningSkills: [...(f.screeningSkills || []), { name, required }] };
        });
    };

    const addAllSuggestedSkills = () => {
        setForm(f => {
            const have = new Set((f.screeningSkills || []).map(x => x.name.toLowerCase()));
            const additions = jdSuggestions.filter(sg => !have.has(sg.name.toLowerCase()));
            if (!additions.length) return f;
            toast.success(`Added ${additions.length} skill${additions.length === 1 ? '' : 's'} to the checklist.`);
            return { ...f, screeningSkills: [...(f.screeningSkills || []), ...additions] };
        });
    };

    if (loadingTemplate) {
        return (
            <div className="mx-auto max-w-[1400px] space-y-3">
                <div className="h-8 w-64 animate-pulse rounded bg-muted" />
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-11 animate-pulse rounded-lg bg-muted/50" />
                ))}
            </div>
        );
    }

    // Examples follow the role being described, so they stay useful outside engineering.
    const rolePlaceholders = placeholdersForRole(aiForm.roleTitle || form.title);
    const companyDefaultLabel = companyDefault === null ? 'Company default' : String(companyDefault);


    const configuredRounds = interviewRounds.filter(r => r.roundMasterId && r.interviewTypeMasterId && r.difficultyMasterId);
    const readyToSchedule  = form.title.trim() && form.jobDescription.trim().length >= 50 && configuredRounds.length > 0;

    return (
        <div className=" max-w-[1400px] space-y-4">
            <FormHeader
                title={isEdit ? 'Edit job description' : 'New job description'}
                subtitle="Describe the role, then set the interview rounds candidates go through."
                onBack={() => navigate('/admin/templates')}
            />

            {formError && (
                <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3.5 py-2.5 text-sm text-destructive">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{formError}
                </div>
            )}

            <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
                <div className="min-w-0 flex-1">
<form id="template-form" onSubmit={handleSubmit} className="space-y-3">
                        {/* AI toggle */}
                        {!editingTemplate && (
                            <div
                                className={cn(
                                    'flex items-center justify-between gap-3 rounded-xl border p-3',
                                    useAI ? 'border-primary/40 bg-primary/5' : 'border-border bg-muted/20'
                                )}
                            >
                                <label
                                    htmlFor="ai-jd-toggle"
                                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-3"
                                >
                                    <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', useAI ? 'bg-primary/15' : 'bg-muted')}>
                                        <Sparkles className={cn('h-4 w-4', useAI ? 'text-primary' : 'text-muted-foreground')} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-foreground">AI Job Description</p>
                                        <p className="text-xs text-muted-foreground">Generate from a role title using AI</p>
                                    </div>
                                </label>
                                <button
                                    id="ai-jd-toggle"
                                    type="button"
                                    role="switch"
                                    aria-checked={useAI}
                                    aria-label="Use AI job description"
                                    onClick={() => { setUseAI(a => !a); if (useAI) setAiForm(EMPTY_AI); }}
                                    className={cn(
                                        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                                        useAI ? 'bg-primary' : 'bg-input'
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'pointer-events-none block h-5 w-5 rounded-full bg-background shadow-md ring-0 transition-transform',
                                            useAI ? 'translate-x-5' : 'translate-x-0'
                                        )}
                                    />
                                </button>
                            </div>
                        )}

                        {useAI && (
                            <div className="space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
                                {/* Row 1: Role title + Experience level */}
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="roleTitle">Job role title <span className="text-destructive">*</span></Label>
                                        <Input
                                            id="roleTitle"
                                            value={aiForm.roleTitle}
                                            onChange={e => setAiForm({ ...aiForm, roleTitle: e.target.value })}
                                            placeholder="e.g. Senior Software Engineer"
                                            className="h-10 bg-background"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>Experience level <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                        <div className="flex flex-wrap gap-1.5">
                                            {EXPERIENCE_LEVELS.map(lvl => (
                                                <button
                                                    key={lvl}
                                                    type="button"
                                                    onClick={() => setAiForm(a => ({ ...a, experienceLevel: a.experienceLevel === lvl ? '' : lvl }))}
                                                    className={cn(
                                                        'rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors',
                                                        aiForm.experienceLevel === lvl
                                                            ? 'border-primary bg-primary text-primary-foreground'
                                                            : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground bg-background'
                                                    )}
                                                >
                                                    {lvl}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Row 2: Key skills */}
                                <div className="space-y-1.5">
                                    <div className="flex items-end justify-between gap-2">
                                        <Label htmlFor="aiSkills">Key skills <span className="text-muted-foreground font-normal">(comma-separated, optional)</span></Label>
                                        <CharCount value={aiForm.requiredSkills} max={AI_LIMITS.requiredSkills} />
                                    </div>
                                    <textarea
                                        id="aiSkills"
                                        value={aiForm.requiredSkills}
                                        onChange={e => setAiForm({ ...aiForm, requiredSkills: e.target.value })}
                                        rows={2}
                                        maxLength={AI_LIMITS.requiredSkills}
                                        placeholder={rolePlaceholders.skills}
                                        className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                    />
                                </div>

                                {/* Row 3: Must-have requirements */}
                                <div className="space-y-1.5">
                                    <div className="flex items-end justify-between gap-2">
                                        <Label htmlFor="aiMustHave">Must-have requirements <span className="text-muted-foreground font-normal">(optional — one per line)</span></Label>
                                        <CharCount value={aiForm.mustHaveRequirements} max={AI_LIMITS.mustHaveRequirements} />
                                    </div>
                                    <textarea
                                        id="aiMustHave"
                                        value={aiForm.mustHaveRequirements}
                                        onChange={e => setAiForm({ ...aiForm, mustHaveRequirements: e.target.value })}
                                        rows={4}
                                        maxLength={AI_LIMITS.mustHaveRequirements}
                                        placeholder={rolePlaceholders.mustHave}
                                        className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                    />
                                </div>

                                {/* Row 4: Additional context */}
                                <div className="space-y-1.5">
                                    <div className="flex items-end justify-between gap-2">
                                        <Label htmlFor="aiContext">Additional context <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                        <CharCount value={aiForm.additionalContext} max={AI_LIMITS.additionalContext} />
                                    </div>
                                    <textarea
                                        id="aiContext"
                                        value={aiForm.additionalContext}
                                        onChange={e => setAiForm({ ...aiForm, additionalContext: e.target.value })}
                                        rows={2}
                                        maxLength={AI_LIMITS.additionalContext}
                                        placeholder={rolePlaceholders.context}
                                        className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                    />
                                </div>

                                <div className="flex items-center justify-between gap-3 pt-1">
                                    <p className="text-xs text-muted-foreground">Review and edit the generated result below before saving.</p>
                                    <Button
                                        type="button"
                                        onClick={handleGenerateJD}
                                        disabled={generatingJD || !aiForm.roleTitle.trim()}
                                        size="sm"
                                        className="shrink-0 gap-2"
                                    >
                                        {generatingJD ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                                        {generatingJD ? 'Generating…' : 'Generate JD'}
                                    </Button>
                                </div>
                                <Separator />
                            </div>
                        )}

                        <div className="grid gap-3 sm:grid-cols-2">
                            <div className="space-y-1.5 sm:col-span-2">
                                <Label htmlFor="title">Title <span className="text-destructive">*</span></Label>
                                <Input id="title" value={form.title} onChange={e => { setForm({ ...form, title: e.target.value }); setFormError(''); }} required className="h-9" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="dept">Department <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                <Input id="dept" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} placeholder="e.g. Engineering" className="h-9" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="pos">Position <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                {/* A datalist suggests the usual answers while still
                                    accepting anything typed. */}
                                <Input
                                    id="pos"
                                    list="position-options"
                                    value={form.position}
                                    onChange={e => setForm({ ...form, position: e.target.value })}
                                    placeholder="Choose or type — e.g. Full-time"
                                    className="h-9"
                                />
                                <datalist id="position-options">
                                    {POSITION_OPTIONS.map(o => <option key={o} value={o} />)}
                                </datalist>
                            </div>
                            <div className="space-y-1.5 sm:col-span-2">
                                <div className="flex flex-wrap items-end justify-between gap-2">
                                    <Label htmlFor="jd">Job description <span className="text-destructive">*</span></Label>
                                    <div className="flex items-center gap-1.5">
                                        <span className={cn('text-[11px] tabular-nums',
                                            form.jobDescription.trim().length < 50 ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground')}>
                                            {form.jobDescription.trim().length.toLocaleString()} characters
                                            {form.jobDescription.trim().length < 50 && ' · 50 minimum'}
                                        </span>
                                        {/* Preview turns the generated markdown into something readable */}
                                        <div className="ml-1 flex rounded-md border border-border p-0.5">
                                            {['edit', 'preview'].map(mode => (
                                                <button
                                                    key={mode}
                                                    type="button"
                                                    onClick={() => setJdView(mode)}
                                                    className={cn('rounded px-2 py-0.5 text-[11px] font-medium capitalize transition-colors',
                                                        jdView === mode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
                                                >
                                                    {mode}
                                                </button>
                                            ))}
                                        </div>
                                        <Button
                                            type="button" variant="ghost" size="icon" className="h-7 w-7"
                                            title="Expand to full screen"
                                            onClick={() => setJdExpanded(true)}
                                        >
                                            <Maximize2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>

                                {jdView === 'edit' ? (
                                    <textarea
                                        id="jd"
                                        value={form.jobDescription}
                                        onChange={e => { setForm({ ...form, jobDescription: e.target.value }); setFormError(''); }}
                                        required
                                        rows={14}
                                        placeholder={useAI ? 'Generated text appears here — review and edit before saving…' : 'Paste or write the job description…'}
                                        className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring min-h-[320px]"
                                    />
                                ) : (
                                    <div className="max-h-[420px] overflow-y-auto rounded-md border border-input bg-muted/20 px-4 py-3">
                                        <JDPreview text={form.jobDescription} />
                                    </div>
                                )}
                            </div>

                            {/* Skills found in the job description — one click adds them
                                to the screening checklist. */}
                            <div className="space-y-2 sm:col-span-2 rounded-xl border border-border bg-muted/20 p-3.5">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                        <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                            <Wand2 className="h-4 w-4 text-primary" />
                                            Skills found in this job description
                                        </p>
                                        <p className="mt-0.5 text-xs text-muted-foreground">
                                            Click one to add it to the screening checklist below.
                                        </p>
                                    </div>
                                    <Button
                                        type="button" variant="outline" size="sm" className="shrink-0 gap-1.5"
                                        onClick={handleAnalyseJD}
                                        disabled={analysing || form.jobDescription.trim().length < 50}
                                        title={form.jobDescription.trim().length < 50 ? 'Write the job description first' : 'Analyse the job description for skills'}
                                    >
                                        {analysing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                                        {analysing ? 'Analysing…' : jdSuggestions.length ? 'Re-scan' : 'Find skills'}
                                    </Button>
                                </div>

                                {jdSuggestions.length > 0 ? (
                                    <div className="flex flex-wrap gap-1.5">
                                        {jdSuggestions.map(({ name, required }) => {
                                            const added = (form.screeningSkills || []).some(x => x.name.toLowerCase() === name.toLowerCase());
                                            return (
                                                <button
                                                    key={name}
                                                    type="button"
                                                    disabled={added}
                                                    onClick={() => addSuggestedSkill(name, required)}
                                                    title={added ? 'Already on the checklist' : `Add "${name}"${required ? ' as required' : ''}`}
                                                    className={cn(
                                                        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                                                        added
                                                            ? 'cursor-default border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                                                            : required
                                                                ? 'border-primary/40 bg-primary/5 text-foreground hover:bg-primary/10'
                                                                : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                                                    )}
                                                >
                                                    {added ? <CheckCircle2 className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                                                    {name}
                                                    {required && !added && <span className="text-[10px] uppercase tracking-wide text-primary">req</span>}
                                                </button>
                                            );
                                        })}
                                        {jdSuggestions.some(sg => !(form.screeningSkills || []).some(x => x.name.toLowerCase() === sg.name.toLowerCase())) && (
                                            <button
                                                type="button"
                                                onClick={addAllSuggestedSkills}
                                                className="inline-flex items-center gap-1 rounded-full border border-dashed border-primary/50 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/5"
                                            >
                                                <Plus className="h-3 w-3" /> Add all
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-xs text-muted-foreground">
                                        {form.jobDescription.trim().length < 50
                                            ? 'Generate or write the job description, then the skills it mentions appear here.'
                                            : 'Nothing scanned yet — use Find skills.'}
                                    </p>
                                )}
                            </div>

                            {/* Pass threshold — per role, inheriting when left blank */}
                            <div className="space-y-2 sm:col-span-2 rounded-xl border border-border bg-muted/20 p-4">
                                <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                    <Target className="h-4 w-4 text-primary" />
                                    Pass threshold <span className="text-muted-foreground font-normal">(optional)</span>
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    The score a candidate must reach for this role — used both to pass an interview round
                                    and to accept a resume. Leave blank to use your company default.
                                </p>
                                <div className="flex flex-wrap items-center gap-3 pt-0.5">
                                    <div className="relative">
                                        <Input
                                            id="passThreshold"
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={form.passThreshold}
                                            onChange={e => { setForm({ ...form, passThreshold: e.target.value }); setFormError(''); }}
                                            placeholder={companyDefaultLabel}
                                            className="h-9 w-36 pr-7"
                                        />
                                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                                    </div>
                                    {String(form.passThreshold).trim() !== '' ? (
                                        <span className="flex items-center gap-1.5 text-xs text-foreground">
                                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                                            This role requires <strong>{form.passThreshold}%</strong>
                                            <button
                                                type="button"
                                                onClick={() => setForm({ ...form, passThreshold: '' })}
                                                className="ml-1 text-muted-foreground underline underline-offset-2 hover:text-foreground"
                                            >
                                                use company default
                                            </button>
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                            <Info className="h-3.5 w-3.5" />
                                            {companyDefault === null
                                                ? 'Using your company default from Interview Settings.'
                                                : <>Using your company default of <strong className="text-foreground">{companyDefault}%</strong>.</>}
                                        </span>
                                    )}
                                </div>
                                {isEdit && (
                                    <p className="text-[11px] text-muted-foreground">
                                        Interviews already scheduled keep the threshold they were booked with — this affects new ones.
                                    </p>
                                )}
                            </div>

                            <div className="space-y-1.5 sm:col-span-2">
                                {/* Suggestions are handled by the section above, which also
                                    marks required vs preferred — passing them here too
                                    would list the same skills twice. */}
                                <ScreeningSkillsEditor
                                    value={form.screeningSkills || []}
                                    onChange={next => setForm({ ...form, screeningSkills: next })}
                                    parsing={Boolean(editingTemplate?.jdCache?.isParsing)}
                                />
                            </div>
                            {/* <div className="space-y-1.5 sm:col-span-2">
                                <Label>Difficulty <span className="text-muted-foreground font-normal">(optional — AI uses this as a hint when generating questions at schedule time)</span></Label>
                                <div className="flex gap-2">
                                    {DIFFICULTY_OPTIONS.map(({ value, label }) => (
                                        <button
                                            key={value || 'none'}
                                            type="button"
                                            onClick={() => setForm({ ...form, difficultyLevel: value })}
                                            className={cn(
                                                'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                                                form.difficultyLevel === value
                                                    ? 'border-primary bg-primary text-primary-foreground'
                                                    : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground'
                                            )}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div> */}

                            {/* Interview rounds blueprint */}
                            <div className="space-y-3 sm:col-span-2 rounded-xl border border-border bg-muted/20 p-4">
                                <div className="flex items-center justify-between gap-2">
                                    <div>
                                        <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                                            <Layers className="h-4 w-4 text-primary" />
                                            Interview rounds <span className="text-destructive">*</span>
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            Define each round from Interview Master. Scheduling uses these settings automatically.
                                        </p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="gap-1 shrink-0"
                                        onClick={addInterviewRound}
                                        disabled={formLoading || mastersLoading || interviewRounds.length >= masters.round.length}
                                    >
                                        <Plus className="h-3.5 w-3.5" />
                                        Add round
                                    </Button>
                                </div>

                                {mastersLoading ? (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                                        <Loader2 className="h-4 w-4 animate-spin" /> Loading masters…
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {interviewRounds.map((round, idx) => {
                                            const used = usedRoundMasterIds(idx);
                                            const roundOpts = masters.round.filter(m => !used.has(m._id) || m._id === round.roundMasterId);
                                            return (
                                                <div key={idx} className="rounded-lg border border-border bg-background p-3 space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                                            Round block {idx + 1}
                                                        </span>
                                                        {interviewRounds.length > 1 && (
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 text-destructive"
                                                                onClick={() => removeInterviewRound(idx)}
                                                                aria-label="Remove round"
                                                            >
                                                                <Trash className="h-3.5 w-3.5" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                    <div className="grid gap-3 sm:grid-cols-3">
                                                        <div className="space-y-1.5">
                                                            <div className="flex items-center justify-between gap-2">
                                                                <Label>Round</Label>
                                                                <InlineAddMaster
                                                                    type="round"
                                                                    disabled={formLoading}
                                                                    onCreated={m => handleMasterCreated('round', m, idx, 'roundMasterId')}
                                                                />
                                                            </div>
                                                            <select
                                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                                                value={round.roundMasterId}
                                                                onChange={e => updateInterviewRound(idx, 'roundMasterId', e.target.value)}
                                                                required
                                                            >
                                                                <option value="">Select round…</option>
                                                                {roundOpts.map(m => (
                                                                    <option key={m._id} value={m._id}>{m.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="space-y-1.5">
                                                            <div className="flex items-center justify-between gap-2">
                                                                <Label>Interview type</Label>
                                                                <InlineAddMaster
                                                                    type="interviewType"
                                                                    disabled={formLoading}
                                                                    onCreated={m => handleMasterCreated('interviewType', m, idx, 'interviewTypeMasterId')}
                                                                />
                                                            </div>
                                                            <select
                                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                                                value={round.interviewTypeMasterId}
                                                                onChange={e => updateInterviewRound(idx, 'interviewTypeMasterId', e.target.value)}
                                                                required
                                                            >
                                                                <option value="">Select type…</option>
                                                                {masters.interviewType.map(m => (
                                                                    <option key={m._id} value={m._id}>{m.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="space-y-1.5">
                                                            <div className="flex items-center justify-between gap-2">
                                                                <Label>Difficulty</Label>
                                                                <InlineAddMaster
                                                                    type="difficulty"
                                                                    disabled={formLoading}
                                                                    onCreated={m => handleMasterCreated('difficulty', m, idx, 'difficultyMasterId')}
                                                                />
                                                            </div>
                                                            <select
                                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                                                value={round.difficultyMasterId}
                                                                onChange={e => updateInterviewRound(idx, 'difficultyMasterId', e.target.value)}
                                                                required
                                                            >
                                                                <option value="">Select difficulty…</option>
                                                                {masters.difficulty.map(m => (
                                                                    <option key={m._id} value={m._id}>{m.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="grid gap-3 sm:grid-cols-2">
                                                        <div className="space-y-1.5">
                                                            <Label>Questions</Label>
                                                            <Input
                                                                type="number"
                                                                min={1}
                                                                max={30}
                                                                value={round.numberOfQuestions}
                                                                onChange={e => updateInterviewRound(idx, 'numberOfQuestions', e.target.value)}
                                                                className="h-9"
                                                            />
                                                        </div>
                                                        <div className="space-y-1.5">
                                                            <Label>Duration (min)</Label>
                                                            <Input
                                                                type="number"
                                                                min={5}
                                                                max={240}
                                                                value={round.durationMinutes}
                                                                onChange={e => updateInterviewRound(idx, 'durationMinutes', e.target.value)}
                                                                className="h-9"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </form>
                </div>

                {/* ── Summary ───────────────────────────────────────────── */}
                <aside className="w-full shrink-0 xl:sticky xl:top-4 xl:w-[21rem]">
                    <div className="rounded-xl border border-border bg-card p-3.5">
                        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                            Summary
                        </p>
                        <dl className="space-y-2 text-xs">
                            {[
                                { icon: FileText, label: 'Title',       value: form.title || null },
                                { icon: FileText, label: 'Description', value: form.jobDescription
                                    ? `${form.jobDescription.trim().length.toLocaleString()} characters` : null },
                                { icon: Hash,     label: 'Screening skills', value: form.screeningSkills?.length
                                    ? `${form.screeningSkills.length} (${form.screeningSkills.filter(s => s.required).length} required)` : 'None' },
                                { icon: Layers,   label: 'Interview rounds', value: configuredRounds.length
                                    ? `${configuredRounds.length} configured` : null },
                            ].map(row => (
                                <div key={row.label} className="flex items-start gap-2">
                                    <row.icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    <div className="min-w-0 flex-1">
                                        <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{row.label}</dt>
                                        <dd className={cn('truncate font-medium', row.value ? 'text-foreground' : 'text-muted-foreground/50')}>
                                            {row.value || 'Not set'}
                                        </dd>
                                    </div>
                                </div>
                            ))}
                        </dl>

                        <div className={cn(
                            'mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-[11px] leading-relaxed',
                            readyToSchedule
                                ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                                : 'border-border bg-muted/40 text-muted-foreground',
                        )}>
                            {readyToSchedule
                                ? <><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />Ready — you can schedule interviews against this role.</>
                                : <><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />Add a title, a description of at least 50 characters, and one interview round before you can schedule against this role.</>}
                        </div>

                        <Button type="submit" form="template-form" className="mt-3 w-full gap-2" disabled={formLoading}>
                            {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {formLoading ? 'Saving…' : isEdit ? 'Save changes' : 'Create job description'}
                        </Button>
                    </div>
                </aside>
            </div>

            {/* Full-screen editing — the same value, just more room */}
            {jdExpanded && (
                <div className="fixed inset-0 z-50 flex flex-col bg-background">
                    <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
                        <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-foreground">
                                {form.title || 'Job description'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {form.jobDescription.trim().length.toLocaleString()} characters · changes save with the form
                            </p>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="flex rounded-md border border-border p-0.5">
                                {['edit', 'preview'].map(mode => (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => setJdView(mode)}
                                        className={cn('rounded px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                                            jdView === mode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
                                    >
                                        {mode}
                                    </button>
                                ))}
                            </div>
                            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setJdExpanded(false)}>
                                <X className="h-3.5 w-3.5" /> Close
                            </Button>
                        </div>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                        <div className="mx-auto max-w-3xl">
                            {jdView === 'edit' ? (
                                <textarea
                                    autoFocus
                                    value={form.jobDescription}
                                    onChange={e => { setForm({ ...form, jobDescription: e.target.value }); setFormError(''); }}
                                    className="h-[calc(100vh-9rem)] w-full resize-none rounded-md border border-input bg-background px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring"
                                />
                            ) : (
                                <JDPreview text={form.jobDescription} />
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={pipelineRoundConfirmOpen}
                onOpenChange={o => { if (!o && !formLoading) { setPipelineRoundConfirmOpen(false); setPendingSavePayload(null); } }}
                title="Candidates in pipeline"
                description="This job description already has candidates in the interview pipeline. Changing interview rounds may affect how future rounds are scheduled. Existing scheduled interviews are not cancelled automatically. Do you want to save these round changes anyway?"
                confirmLabel="Save changes"
                cancelLabel="Cancel"
                loading={formLoading}
                onConfirm={handleConfirmPipelineRoundChange}
            />

            <ConfirmDialog
                open={confirmGenerate}
                onOpenChange={setConfirmGenerate}
                title="Replace the existing description?"
                description="Generating will overwrite the job description you have written. This cannot be undone."
                confirmLabel="Generate"
                onConfirm={() => { setConfirmGenerate(false); handleGenerateJD(); }}
            />
        </div>
    );
}
